import { confirmDialog, notify } from '../utils/notify';
// ============================================================================
// OVERTIME CONFIGURATION PAGE - frontend/src/pages/OvertimeConfiguration.js
// ============================================================================

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { overtimeAPI } from '../services/api';
import { Calendar, CalendarDays, Globe, PartyPopper, Pencil, Plus, Power, Sun, Trash2 } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './OvertimeConfiguration.css';

const OvertimeConfiguration = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    overtime_type: '',
    code: '',
    description: '',
    multiplier: '',
    applies_to: 'all',
    min_hours: '',
    max_hours: '',
    requires_approval: true,
    time_start: '',
    time_end: '',
    day_type: 'any',
    calculation_order: '',
    is_active: true,
    is_mandatory: false
  });

  const canManage = ['super_admin', 'hr_manager', 'operations_manager'].includes(user?.role);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await overtimeAPI.getConfigurations();
      if (response.data.success) {
        setConfigurations(response.data.configurations);
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      notify(t('Error loading overtime configurations'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
          ...formData,
          code: formData.code.toUpperCase(),
          multiplier: parseFloat(formData.multiplier),
          min_hours: formData.min_hours ? parseFloat(formData.min_hours) : 0,
          max_hours: formData.max_hours ? parseFloat(formData.max_hours) : null,
          calculation_order: formData.calculation_order ? parseInt(formData.calculation_order) : 0
      };

      const response = editingConfig
        ? await overtimeAPI.updateConfiguration(editingConfig.id, payload)
        : await overtimeAPI.createConfiguration(payload);

      if (response.data.success) {
        notify(editingConfig ? t('Configuration updated!') : t('Configuration created!'), 'success');
        setShowModal(false);
        resetForm();
        loadConfigurations();
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      notify(t('Error saving configuration'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Delete this overtime configuration? This cannot be undone.'))) return;

    try {
      const response = await overtimeAPI.deleteConfiguration(id);

      if (response.data.success) {
        notify(t('Configuration deleted successfully'), 'success');
        loadConfigurations();
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error deleting configuration:', error);
      notify(t('Error deleting configuration'), 'error');
    }
  };

  const handleToggleStatus = async (config) => {
    try {
      const response = await overtimeAPI.toggleConfiguration(config.id);

      if (response.data.success) {
        loadConfigurations();
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      notify(t('Error updating status'), 'error');
    }
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      overtime_type: config.overtime_type,
      code: config.code,
      description: config.description || '',
      multiplier: config.multiplier,
      applies_to: config.applies_to || 'all',
      min_hours: config.min_hours || '',
      max_hours: config.max_hours || '',
      requires_approval: config.requires_approval,
      time_start: config.time_start || '',
      time_end: config.time_end || '',
      day_type: config.day_type || 'any',
      calculation_order: config.calculation_order || '',
      is_active: config.is_active,
      is_mandatory: config.is_mandatory
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      overtime_type: '',
      code: '',
      description: '',
      multiplier: '',
      applies_to: 'all',
      min_hours: '',
      max_hours: '',
      requires_approval: true,
      time_start: '',
      time_end: '',
      day_type: 'any',
      calculation_order: '',
      is_active: true,
      is_mandatory: false
    });
    setEditingConfig(null);
  };

  const getMultiplierBadge = (multiplier) => {
    const value = parseFloat(multiplier);
    if (value >= 2.5) return { tone: 'danger', label: t('{value}x - Premium', { value }) };
    if (value >= 2.0) return { tone: 'warning', label: t('{value}x - High', { value }) };
    if (value >= 1.5) return { tone: 'success', label: t('{value}x - Standard', { value }) };
    return { tone: 'neutral', label: `${value}x` };
  };

  const getDayTypeIcon = (dayType) => {
    const icons = {
      'weekday': CalendarDays,
      'weekend': Sun,
      'holiday': PartyPopper,
      'any': Globe
    };
    const DayIcon = icons[dayType] || Calendar;
    return <DayIcon aria-hidden="true" />;
  };

  return (
    <div className="overtime-config-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Overtime Configuration')}</h1>
          <p className="page-sub">{t('Manage overtime rates and policies')}</p>
        </div>
        {canManage && (
          <button 
            onClick={() => { resetForm(); setShowModal(true); }} 
            className="btn btn--primary"
          >
            <Plus aria-hidden="true" /> {t('Add Overtime Type')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="empty">{t('Loading configurations...')}</div>
      ) : (
        <div className="oc-grid">
          {configurations.map(config => {
            const multiplierInfo = getMultiplierBadge(config.multiplier);
            
            return (
              <div 
                key={config.id} 
                className={`card oc-card${!config.is_active ? ' is-inactive' : ''}`}
              >
                <div className="oc-card__head">
                  <div className="oc-card__who">
                    <h3>
                      {getDayTypeIcon(config.day_type)} {config.overtime_type}
                    </h3>
                    <span className="oc-card__code">{config.code}</span>
                  </div>
                  <StatusBadge status={config.is_active ? 'active' : 'inactive'}>
                    {config.is_active ? t('Active') : t('Inactive')}
                  </StatusBadge>
                </div>

                <div className="oc-card__info">
                  <div className="oc-row">
                    <span className="oc-row__label">{t('Rate Multiplier:')}</span>
                    <span className={`oc-row__value oc-multiplier is-${multiplierInfo.tone}`}>
                      {multiplierInfo.label}
                    </span>
                  </div>

                  <div className="oc-row">
                    <span className="oc-row__label">{t('Applies To:')}</span>
                    <span className="oc-row__value">{
                      config.applies_to === 'all' ? t('All Employees')
                      : config.applies_to === 'hourly' ? t('Hourly Only')
                      : config.applies_to === 'monthly' ? t('Monthly Only')
                      : config.applies_to
                    }</span>
                  </div>

                  <div className="oc-row">
                    <span className="oc-row__label">{t('Day Type:')}</span>
                    <span className="oc-row__value">
                      {getDayTypeIcon(config.day_type)} {
                        config.day_type === 'any' ? t('Any Day')
                        : config.day_type === 'weekday' ? t('Weekdays Only')
                        : config.day_type === 'weekend' ? t('Weekends Only')
                        : config.day_type === 'holiday' ? t('Holidays Only')
                        : config.day_type
                      }
                    </span>
                  </div>

                  {(config.time_start || config.time_end) && (
                    <div className="oc-row">
                      <span className="oc-row__label">{t('Time Window:')}</span>
                      <span className="oc-row__value">
                        {config.time_start || '00:00'} - {config.time_end || '23:59'}
                      </span>
                    </div>
                  )}

                  {config.min_hours > 0 && (
                    <div className="oc-row">
                      <span className="oc-row__label">{t('Min Hours:')}</span>
                      <span className="oc-row__value">{config.min_hours}h</span>
                    </div>
                  )}

                  {config.max_hours && (
                    <div className="oc-row">
                      <span className="oc-row__label">{t('Max Hours:')}</span>
                      <span className="oc-row__value">{config.max_hours}h</span>
                    </div>
                  )}

                  <div className="oc-row">
                    <span className="oc-row__label">{t('Requires Approval:')}</span>
                    <span className="oc-row__value">
                      {config.requires_approval ? t('Yes') : t('No')}
                    </span>
                  </div>

                  {config.description && (
                    <div className="oc-row">
                      <span className="oc-row__label">{t('Description:')}</span>
                      <span className="oc-row__value">{config.description}</span>
                    </div>
                  )}
                </div>

                {canManage && (
                  <div className="oc-card__actions">
                    <button 
                      onClick={() => openEditModal(config)}
                      className="btn btn--secondary"
                      title={t('Edit')}
                    >
                      <Pencil aria-hidden="true" /> {t('Edit')}
                    </button>
                    <button 
                      onClick={() => handleToggleStatus(config)}
                      className="btn btn--secondary"
                      title={config.is_active ? t('Deactivate') : t('Activate')}
                    >
                      <Power aria-hidden="true" /> {config.is_active ? t('Deactivate') : t('Activate')}
                    </button>
                    <button 
                      onClick={() => handleDelete(config.id)}
                      className="btn btn--secondary oc-danger"
                      title={t('Delete')}
                    >
                      <Trash2 aria-hidden="true" /> {t('Delete')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {configurations.length === 0 && (
            <div className="empty">
              <p>{t('No overtime configurations')}</p>
              {canManage && (
                <button onClick={() => setShowModal(true)} className="btn btn--primary">
                  {t('Add First Configuration')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <Modal open title={editingConfig ? t('Edit Overtime Configuration') : t('Create Overtime Configuration')} onClose={() => { setShowModal(false); resetForm(); }} size="md">

            <div className="oc-dialog">
              <form onSubmit={handleSubmit}>
                <div className="oc-field">
                  <label className="field-label">{t('Overtime Type *')}</label>
                  <input className="input"
                    type="text"
                    value={formData.overtime_type}
                    onChange={(e) => setFormData({...formData, overtime_type: e.target.value})}
                    placeholder={t('e.g., Regular Overtime, Weekend Overtime')}
                    required
                  />
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Code *')}</label>
                  <input className="input"
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder={t('e.g., OT_REGULAR')}
                    required
                  />
                  <small className="field-hint">{t('Unique identifier (will be converted to uppercase)')}</small>
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Rate Multiplier *')}</label>
                  <input className="input"
                    type="number"
                    step="0.01"
                    min="1"
                    max="5"
                    value={formData.multiplier}
                    onChange={(e) => setFormData({...formData, multiplier: e.target.value})}
                    placeholder="1.5"
                    required
                  />
                  <small className="field-hint">{t('1.5x = time and a half, 2.0x = double time, etc.')}</small>
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Applies To *')}</label>
                  <select className="select"
                    value={formData.applies_to}
                    onChange={(e) => setFormData({...formData, applies_to: e.target.value})}
                    required
                  >
                    <option value="all">{t('All Employees')}</option>
                    <option value="hourly">{t('Hourly Only')}</option>
                    <option value="monthly">{t('Monthly Only')}</option>
                  </select>
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Day Type *')}</label>
                  <select className="select"
                    value={formData.day_type}
                    onChange={(e) => setFormData({...formData, day_type: e.target.value})}
                    required
                  >
                    <option value="any">{t('Any Day')}</option>
                    <option value="weekday">{t('Weekdays Only')}</option>
                    <option value="weekend">{t('Weekends Only')}</option>
                    <option value="holiday">{t('Holidays Only')}</option>
                  </select>
                </div>

                <div className="oc-form-row">
                  <div className="oc-field">
                    <label className="field-label">{t('Start Time')}</label>
                    <input className="input"
                      type="time"
                      value={formData.time_start}
                      onChange={(e) => setFormData({...formData, time_start: e.target.value})}
                    />
                    <small className="field-hint">{t('Optional - when overtime period starts')}</small>
                  </div>

                  <div className="oc-field">
                    <label className="field-label">{t('End Time')}</label>
                    <input className="input"
                      type="time"
                      value={formData.time_end}
                      onChange={(e) => setFormData({...formData, time_end: e.target.value})}
                    />
                    <small className="field-hint">{t('Optional - when overtime period ends')}</small>
                  </div>
                </div>

                <div className="oc-form-row">
                  <div className="oc-field">
                    <label className="field-label">{t('Minimum Hours')}</label>
                    <input className="input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.min_hours}
                      onChange={(e) => setFormData({...formData, min_hours: e.target.value})}
                      placeholder="0"
                    />
                    <small className="field-hint">{t('Minimum hours to qualify')}</small>
                  </div>

                  <div className="oc-field">
                    <label className="field-label">{t('Maximum Hours')}</label>
                    <input className="input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.max_hours}
                      onChange={(e) => setFormData({...formData, max_hours: e.target.value})}
                      placeholder={t('Optional')}
                    />
                    <small className="field-hint">{t('Max hours allowed per period')}</small>
                  </div>
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Calculation Order')}</label>
                  <input className="input"
                    type="number"
                    value={formData.calculation_order}
                    onChange={(e) => setFormData({...formData, calculation_order: e.target.value})}
                    placeholder="0"
                  />
                  <small className="field-hint">{t('Lower numbers are checked first (0 = highest priority)')}</small>
                </div>

                <div className="oc-field">
                  <label className="field-label">{t('Description')}</label>
                  <textarea className="textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t('Brief description of this overtime type')}
                    rows="3"
                  />
                </div>

                <div className="oc-field">
                  <label className="oc-check">
                    <input
                      type="checkbox"
                      checked={formData.requires_approval}
                      onChange={(e) => setFormData({...formData, requires_approval: e.target.checked})}
                    />
                    <span>{t('Requires Approval')}</span>
                  </label>
                </div>

                <div className="oc-field">
                  <label className="oc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span>{t('Active')}</span>
                  </label>
                </div>

                <div className="oc-field">
                  <label className="oc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({...formData, is_mandatory: e.target.checked})}
                    />
                    <span>{t('Mandatory')}</span>
                  </label>
                </div>

                <div className="oc-form-actions">
                  <button 
                    type="button" 
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="btn btn--secondary"
                  >
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary">
                    {editingConfig ? t('Update Configuration') : t('Create Configuration')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default OvertimeConfiguration;